import React, { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays, X } from "lucide-react";
import { fmtDate, todayStr } from "@/lib/constants";
import { cn } from "@/lib/utils";

// Value/onChange use a plain "YYYY-MM-DD" string (no time, no tz drift).
const toStr = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const toDate = (s) => {
  if (!s) return undefined;
  const d = new Date(s + "T00:00:00");
  return isNaN(d) ? undefined : d;
};

// Flexible keyboard parsing: 26/9, 26/09/26, 26-09-2026, 2026-09-26 …
const parseTyped = (raw) => {
  const s = (raw || "").trim();
  if (!s) return null;
  const norm = (y, mo, d) => {
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
    const dt = new Date(y, mo - 1, d);
    return dt.getMonth() === mo - 1 ? toStr(dt) : null; // rejects e.g. 31/02
  };
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return norm(+m[1], +m[2], +m[3]);
  m = s.match(/^(\d{1,2})[/.\-](\d{1,2})(?:[/.\-](\d{2}|\d{4}))?$/);
  if (m) {
    const y = m[3] ? (m[3].length === 2 ? 2000 + +m[3] : +m[3]) : new Date().getFullYear();
    return norm(y, +m[2], +m[1]);
  }
  return null;
};

// Quick presets anchored to Sydney "today" (matches the rest of the app).
const buildPresets = (today) => {
  const add = (n) => { const d = new Date(today); d.setDate(d.getDate() + n); return d; };
  const nextFriday = () => {
    const d = new Date(today);
    do { d.setDate(d.getDate() + 1); } while (d.getDay() !== 5); // strictly after today
    return d;
  };
  return [
    { label: "Today", date: today },
    { label: "Tomorrow", date: add(1) },
    { label: "Next Friday", date: nextFriday() },
  ];
};

// Calendar-first date field: pick from the calendar/presets, or type as a fast-path.
// Past-due days are tinted red. Pass `children` to use a custom trigger.
export function DatePicker({ value, onChange, placeholder = "Select date", disabled, testId, children, align = "start" }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [typeErr, setTypeErr] = useState(false);
  const today = toDate(todayStr());
  const selected = toDate(value);
  const isPast = selected && selected < today;

  const pick = (d) => { onChange(toStr(d)); setTyped(""); setTypeErr(false); setOpen(false); };
  const commitTyped = () => {
    if (!typed.trim()) return;
    const parsed = parseTyped(typed);
    if (parsed) { pick(toDate(parsed)); } else { setTypeErr(true); }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setTyped(""); setTypeErr(false); } }}>
      <PopoverTrigger asChild>
        {children || (
          <button type="button" disabled={disabled} data-testid={testId}
            className={cn(
              "mt-1.5 flex h-9 w-full items-center gap-1.5 rounded-sm border border-input bg-white px-2.5 text-sm text-left transition-colors hover:bg-zinc-50",
              disabled && "opacity-50 cursor-not-allowed"
            )}>
            <CalendarDays className="h-4 w-4 text-zinc-400 shrink-0" />
            <span className={cn("flex-1 min-w-0 whitespace-nowrap", !selected && "truncate text-zinc-400", isPast ? "text-rose-600 font-medium" : selected && "text-zinc-900")}>
              {selected ? fmtDate(value) : placeholder}
            </span>
            {selected && !disabled && (
              <span role="button" tabIndex={-1} data-testid={testId ? `${testId}-clear` : undefined}
                onClick={(e) => { e.stopPropagation(); onChange(""); }}
                className="shrink-0 text-zinc-300 hover:text-zinc-600">
                <X className="h-3.5 w-3.5" />
              </span>
            )}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <div className="border-b border-zinc-100 p-2 space-y-2">
          <input value={typed} data-testid={testId ? `${testId}-typed` : "datepicker-typed"}
            onChange={(e) => { setTyped(e.target.value); setTypeErr(false); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitTyped(); } }}
            onBlur={commitTyped}
            placeholder="Type e.g. 26/09/26"
            className={cn("h-8 w-full rounded-sm border px-2 text-xs outline-none focus:border-zinc-400",
              typeErr ? "border-rose-400" : "border-zinc-200")} />
          <div className="flex flex-wrap gap-1.5">
            {buildPresets(today).map((p) => (
              <button key={p.label} type="button" onClick={() => pick(p.date)}
                data-testid={testId ? `${testId}-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}` : undefined}
                className="rounded-full border border-zinc-200 px-2.5 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-900 hover:text-white hover:border-zinc-900 transition-colors">
                {p.label}
              </button>
            ))}
            {selected && (
              <button type="button" onClick={() => { onChange(""); setOpen(false); }}
                data-testid={testId ? `${testId}-preset-clear` : undefined}
                className="rounded-full px-2.5 py-1 text-xs font-medium text-zinc-400 hover:text-zinc-700">
                Clear
              </button>
            )}
          </div>
        </div>
        <Calendar mode="single" selected={selected} defaultMonth={selected}
          onSelect={(d) => d && pick(d)} initialFocus
          modifiers={{ overdue: { before: today } }}
          modifiersClassNames={{ overdue: "text-rose-500" }} />
      </PopoverContent>
    </Popover>
  );
}
